/**
 * Reglas de seguridad de las contraseñas, copia fiel de la política de la API
 * (`src/modules/auth/password.policy.ts`).
 *
 * Se duplica a propósito: el ERP marca cada regla en verde mientras se escribe,
 * y pedirle al servidor una validación por tecla pulsada sería una petición por
 * carácter. Esta copia solo sirve para guiar; quien decide es la API, que
 * vuelve a comprobarlo todo y responde con las reglas incumplidas.
 *
 * Si cambian allí, cambian aquí: `GET /auth/password-rules` devuelve las
 * etiquetas vigentes y permite detectar la divergencia.
 */

export const PASSWORD_MIN = 10;

/** bcrypt solo mira los primeros 72 bytes; aceptar más sería engañoso. */
export const PASSWORD_MAX = 72;

const PROHIBIDAS = [
  'password', 'passw0rd', 'contrasena', 'contraseña', 'qwerty', 'asdfgh',
  'abcdef', '123456', '654321', 'admin', 'administrador', 'usuario',
  'bienvenido', 'welcome', 'iloveyou', 'letmein', 'mexico', 'erp'
];

export interface PasswordRule {
  id: string;
  label: string;
  test: (password: string, email: string) => boolean;
}

/** true si hay 4 o más caracteres consecutivos ("1234", "dcba"). */
function tieneSecuencia(password: string): boolean {
  const texto = password.toLowerCase();
  let seguidos = 1;
  for (let i = 1; i < texto.length; i++) {
    const salto = texto.charCodeAt(i) - texto.charCodeAt(i - 1);
    seguidos = salto === 1 || salto === -1 ? seguidos + 1 : 1;
    if (seguidos >= 4) return true;
  }
  return false;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'longitud',
    label: `Entre ${PASSWORD_MIN} y ${PASSWORD_MAX} caracteres`,
    test: p => p.length >= PASSWORD_MIN && p.length <= PASSWORD_MAX
  },
  { id: 'minuscula', label: 'Al menos una minúscula', test: p => /[a-záéíóúñü]/.test(p) },
  { id: 'mayuscula', label: 'Al menos una mayúscula', test: p => /[A-ZÁÉÍÓÚÑÜ]/.test(p) },
  { id: 'numero',    label: 'Al menos un número',     test: p => /[0-9]/.test(p) },
  {
    id: 'simbolo',
    label: 'Al menos un símbolo (! @ # $ % & * ?)',
    test: p => /[^A-Za-z0-9áéíóúñüÁÉÍÓÚÑÜ]/.test(p)
  },
  { id: 'sinEspaciosExtremos', label: 'Sin espacios al principio ni al final', test: p => p === p.trim() },
  { id: 'sinRepeticiones', label: 'Sin el mismo carácter repetido tres veces seguidas', test: p => !/(.)\1\1/.test(p) },
  { id: 'sinSecuencias', label: 'Sin secuencias como "1234" o "abcd"', test: p => !tieneSecuencia(p) },
  {
    id: 'sinCorreo',
    label: 'No puede contener tu correo ni tu nombre de usuario',
    test: (p, email) => {
      const local = (email ?? '').split('@')[0]?.toLowerCase() ?? '';
      return local.length < 3 || !p.toLowerCase().includes(local);
    }
  },
  {
    id: 'noComun',
    label: 'No puede ser una contraseña común',
    test: p => !PROHIBIDAS.some(mala => p.toLowerCase().includes(mala))
  }
];

/** Estado de cada regla para pintar la lista de requisitos. */
export function evaluatePassword(password: string, email = ''): { id: string; label: string; ok: boolean }[] {
  return PASSWORD_RULES.map(rule => ({
    id: rule.id,
    label: rule.label,
    ok: password.length > 0 && rule.test(password, email)
  }));
}

export function passwordIsValid(password: string, email = ''): boolean {
  return PASSWORD_RULES.every(rule => rule.test(password, email));
}

/**
 * Fuerza estimada, de 0 a 4.
 *
 * Mide longitud y variedad, no si cumple la política: sirve para animar a
 * pasar del mínimo. Una contraseña puede ser válida y aun así salir "regular",
 * que es justo el mensaje que se quiere dar con diez caracteres justos.
 */
export function passwordStrength(password: string): 0 | 1 | 2 | 3 | 4 {
  if (!password) return 0;

  const familias = [/[a-záéíóúñü]/, /[A-ZÁÉÍÓÚÑÜ]/, /[0-9]/, /[^A-Za-z0-9]/]
    .filter(re => re.test(password)).length;

  let puntos = familias;
  if (password.length >= 12) puntos++;
  if (password.length >= 16) puntos++;
  if (password.length < PASSWORD_MIN) puntos = Math.min(puntos, 1);

  return Math.max(0, Math.min(4, puntos - 1)) as 0 | 1 | 2 | 3 | 4;
}

export const STRENGTH_LABEL: Record<number, string> = {
  0: 'Muy débil',
  1: 'Débil',
  2: 'Regular',
  3: 'Fuerte',
  4: 'Muy fuerte'
};
